/**
 * ============================================================================
 * TEMPORARY FIXTURE DATA — NOT A LIVE RATE SOURCE.
 * ============================================================================
 *
 * This file exists so the Convert screen can be built and tested before the
 * rate engine is wired. It is replaced wholesale in the next step by a real
 * snapshot fetched from Frankfurter, cached in IndexedDB and refreshed by the
 * Worker's cron trigger. Nothing outside src/rates/ should import it directly.
 *
 * The values ARE real: fetched from https://api.frankfurter.dev/v2/rates on
 * 2026-08-09 and pasted verbatim, so precision, magnitude and — importantly —
 * the per-currency dates behave like production data rather than like round
 * numbers someone invented.
 *
 * Note EGP: its rate is from 2026-08-07 while everything else is 2026-08-09.
 * That is not a mistake in the fixture. Central banks publish on their own
 * schedules, so a snapshot genuinely has no single "as of" date. The status row
 * reports the OLDEST date among visible rows, and rows carrying an older date
 * say so quietly. Keep this asymmetry in the fixture — it is the only reason
 * that behaviour is testable before the engine exists.
 */

import type { Observation } from "./coverage";

export interface RateEntry {
  /** Units of this currency per 1 USD, as a string to avoid float loss. */
  readonly rate: string;
  /** ISO date the publishing authority quoted this rate. */
  readonly date: string;
  /**
   * What each provider actually returned. Coverage is DERIVED from these
   * rather than declared, so the fixture exercises the real derivation instead
   * of asserting a verdict someone typed.
   */
  readonly observations: readonly Observation[];
}

/** Three providers agreeing, which is the ordinary case. */
function agreed(rate: string, date: string): readonly Observation[] {
  const n = Number(rate);
  return [
    { provider: "frankfurter", rate, date },
    { provider: "erapi", rate: (n * 1.001).toPrecision(8), date },
    { provider: "fawaz", rate: (n * 0.9995).toPrecision(8), date },
  ];
}

export interface RateSnapshot {
  readonly base: "USD";
  readonly rates: Readonly<Record<string, RateEntry>>;
  /** Where these came from, shown in the UI. Accuracy is the product. */
  readonly source: string;
  /** True while running on fixture data, so the UI can be honest about it. */
  readonly isFixture: boolean;
}

export const FIXTURE_SNAPSHOT: RateSnapshot = {
  base: "USD",
  source: "Frankfurter (fixture)",
  isFixture: true,
  rates: {
    USD: { rate: "1", date: "2026-08-09", observations: agreed("1", "2026-08-09") },
    EUR: { rate: "0.86614", date: "2026-08-09", observations: agreed("0.86614", "2026-08-09") },
    GBP: { rate: "0.74307", date: "2026-08-09", observations: agreed("0.74307", "2026-08-09") },
    JPY: { rate: "158.17", date: "2026-08-09", observations: agreed("158.17", "2026-08-09") },
    SAR: { rate: "3.75", date: "2026-08-09", observations: agreed("3.75", "2026-08-09") },
    AED: { rate: "3.6725", date: "2026-08-09", observations: agreed("3.6725", "2026-08-09") },
    EGP: { rate: "49.782", date: "2026-08-07", observations: agreed("49.782", "2026-08-07") },
    CHF: { rate: "0.81001", date: "2026-08-09", observations: agreed("0.81001", "2026-08-09") },
    CAD: { rate: "1.4002", date: "2026-08-09", observations: agreed("1.4002", "2026-08-09") },
    AUD: { rate: "1.421", date: "2026-08-09", observations: agreed("1.421", "2026-08-09") },
    INR: { rate: "95.21", date: "2026-08-09", observations: agreed("95.21", "2026-08-09") },
    KWD: { rate: "0.30775", date: "2026-08-09", observations: agreed("0.30775", "2026-08-09") },

    /*
     * The two coverage states, with the REAL provider figures measured on
     * 2026-08-13. They are in the fixture so the states can be seen on the
     * actual screen rather than only asserted in a test.
     */

    // Sudan: a parallel market. All three providers are honest and they still
    // differ by 10.4%. Shown, with context.
    SDG: {
      rate: "600.186",
      date: "2026-08-09",
      observations: [
        { provider: "frankfurter", rate: "600.5", date: "2026-08-09" },
        { provider: "erapi", rate: "544.017", date: "2026-08-09" },
        { provider: "fawaz", rate: "600.186", date: "2026-08-09" },
      ],
    },

    // Syria: redenominated. Two providers quote the new unit, one the old, and
    // the gap is 10,577%. Withheld — we do not know which unit is meant.
    SYP: {
      rate: "122.1",
      date: "2026-08-09",
      observations: [
        { provider: "frankfurter", rate: "122.1", date: "2026-08-09" },
        { provider: "erapi", rate: "121.82", date: "2026-08-09" },
        { provider: "fawaz", rate: "13006.5", date: "2026-08-09" },
      ],
    },
  },
};

export const FIXTURE_CODES: readonly string[] = Object.keys(FIXTURE_SNAPSHOT.rates);
